import React__default from 'react';
import { Range, Editor, Transforms, Text, Path } from 'slate';
import { Type, MatchPhase, Attribute } from './types.mjs';
import { isTokenWrapperElement, isBlockTokenElement, createTokenElement, createTokenWrapperElement, isInlineTokenElement, isTokenElement, ZERO_WIDTH_SPACE } from './utilities.mjs';
import { elementIsEmpty } from './queries/elementIsEmpty.mjs';
import { insertToken } from './transforms/insertToken.mjs';
import { isDocumentRoot } from '../../../../core/queries/isDocumentRoot.mjs';
import { Token } from './components/Token/Token.mjs';
import { isBlock } from '../../../../core/queries/isBlock.mjs';
import { isElement } from '../../../../core/utilities/nodes/elements/isElement.mjs';
import { createElement } from '../../../../core/utilities/nodes/elements/createElement.mjs';
import { findNodes } from '../../../../core/queries/findNode.mjs';
import { getRenderElement } from '../../../../core/rendering/getRenderElement.mjs';
import { getNodeDeserializer } from '../../../../core/serialization/deserialize/utilities/getNodeDeserializer.mjs';

const element = 'shopify-token';
const TokenPlugin = ({
  getAttributes,
  onTokenClick,
  renderPopover,
  serialize,
  match,
  limit,
  supportsBlockTokensBeta,
  enableExperimentalTabNavigation,
  defaultBlockType
}) => ({
  type: Type,
  data: {
    limit
  },
  extendEditor(editor) {
    const {
      normalizeNode,
      deleteForward,
      deleteBackward
    } = editor;
    editor.deleteForward = (...args) => {
      var _Editor$above, _Editor$next;
      // Handle when cursor is positioned above a block token and deleteForward is triggered
      const range = editor.selection;

      // Make sure selection is collapsed
      if (!range || !Range.isCollapsed(range)) {
        deleteForward(...args);
        return;
      }

      // Retrieve the highest block element (to account for nested block elements such as lists and list-items)
      // i.e. return the entire list if selection is inside a list-item
      const [parentNode, parentPath] = (_Editor$above = Editor.above(editor, {
        at: range,
        mode: 'highest',
        match: node => isBlock(editor, node) && isElement(node)
      })) != null ? _Editor$above : [];

      // Make sure the next block element represents a token-wrapper
      const [nextNode, nextPath] = (_Editor$next = Editor.next(editor, {
        at: parentPath
      })) != null ? _Editor$next : [];
      if (nextNode && nextPath && isTokenWrapperElement(nextNode)) {
        // Proceed with delete forward when inside an empty paragraph
        if (parentNode && isElement(parentNode) && parentNode.type === defaultBlockType && elementIsEmpty(parentNode)) {
          deleteForward(...args);
          return;
        } else {
          Transforms.select(editor, Editor.start(editor, nextPath));
          return;
        }
      }

      // Continue with deleteForward (on the new selection)
      deleteForward(...args);
    };
    editor.deleteBackward = (...args) => {
      var _Editor$next2;
      // Handle when cursor is positioned on the left edge of a block token and deleteBackward is triggered
      const range = editor.selection;

      // Make sure selection is collapsed
      if (!range || !Range.isCollapsed(range)) {
        deleteBackward(...args);
        return;
      }

      // Make sure selection is inside token wrapper and
      // Make sure selection is in text node at index 0 (or the text node before block token)
      const [parentNode, parentPath] = Editor.parent(editor, range);
      const [nodeAt, nodeAtPath] = Editor.node(editor, range);
      if (!isTokenWrapperElement(parentNode) || !Text.isText(nodeAt)) {
        deleteBackward(...args);
        return;
      }
      const [nextNode] = (_Editor$next2 = Editor.next(editor, {
        at: nodeAtPath
      })) != null ? _Editor$next2 : [];

      // Make sure previous sibling has text content
      if (nextNode && isBlockTokenElement(nextNode)) {
        var _Editor$previous;
        const [nodePrecedingParent, pathOfNodePrecedingParent] = (_Editor$previous = Editor.previous(editor, {
          at: parentPath
        })) != null ? _Editor$previous : [];

        // Move selection backwards if we're positioned at the edge of token-wrapper
        if (nodePrecedingParent && Path.isPath(pathOfNodePrecedingParent) && isElement(nodePrecedingParent) && isBlock(editor, nodePrecedingParent)) {
          if (!elementIsEmpty(nodePrecedingParent)) {
            Transforms.select(editor, Editor.end(editor, pathOfNodePrecedingParent));
          }
        }
      }

      // Continue with deleteBackward (on the new selection)
      deleteBackward(...args);
    };
    editor.normalizeNode = entry => {
      const [node, path] = entry;

      // Handle manually inputted token (i.e. typing {{ token.acesspath}} )
      if (Text.isText(node)) {
        const {
          text,
          ...marks
        } = node;
        const matches = match(text, MatchPhase.Composition);
        if (matches.length) {
          for (const match of matches) {
            const {
              anchor,
              focus,
              value
            } = match;
            const block = Boolean(supportsBlockTokensBeta && match.block);

            // Replace text match with a token element
            const tokenData = createTokenElement({
              value,
              block
            }, marks);
            const at = {
              anchor: {
                path,
                offset: anchor
              },
              focus: {
                path,
                offset: focus
              }
            };
            insertToken(editor, {
              at,
              data: tokenData,
              tokenSerializedValue: serialize(tokenData),
              defaultBlockType
            });
            return;
          }
        }
      }

      // Wrap block tokens in token-wrapper element
      if (isBlockTokenElement(node)) {
        const [parentNode, parentPath] = Editor.parent(editor, path);

        // Wrap block-token in token-wrapper when at the document root
        if (isDocumentRoot(parentNode, parentPath)) {
          Transforms.wrapNodes(editor, createTokenWrapperElement(), {
            at: path
          });
          return;
        }
        const isParentTokenWrapper = isTokenWrapperElement(parentNode);
        const parentHasMoreThanOneBlockToken = parentNode.children.filter(isBlockTokenElement).length > 1;

        // Lift block token when paren
        if (!isParentTokenWrapper || parentHasMoreThanOneBlockToken) {
          Transforms.liftNodes(editor, {
            at: path
          });
          return;
        }
      } else if (supportsBlockTokensBeta && isInlineTokenElement(node)) {
        const [parentNode, parentPath] = Editor.parent(editor, path);

        // If an inline token gets inserted around a block token
        // (i.e. in the empty text nodes inside a token-wrapper)
        // Lift the inline tokens and wrap them in the defaultBlockType
        const blockTokenIndex = parentNode.children.findIndex(isBlockTokenElement);
        if (isTokenWrapperElement(parentNode) && blockTokenIndex !== -1) {
          const pathOfBlockTokenInWrapper = [...parentPath, blockTokenIndex];

          // If insertion happens in empty text node before block token
          // Split the content in order for the inline token to be inserted before the block token
          // Else Lift the node
          if (Path.isBefore(path, pathOfBlockTokenInWrapper)) {
            Transforms.splitNodes(editor, {
              at: path
            });
            return;
          } else {
            Transforms.liftNodes(editor, {
              at: path
            });
            return;
          }
        }
      } else if (isTokenWrapperElement(node)) {
        const {
          children
        } = node;

        // 1. Convert token-wrapper to default block type if it is empty
        // token-wrapper is considered empty when it does not have a block-token
        const blockTokensCount = children.filter(isBlockTokenElement).length;
        if (blockTokensCount === 0) {
          Transforms.setNodes(editor, createElement(defaultBlockType, {}), {
            at: path
          });
          return;
        }

        // 2. Ensure token-wrapper element only contains empty-text and a single block token
        // Lift text nodes that are non-empty
        for (const [index, child] of children.entries()) {
          if (Text.isText(child) && child.text !== '' || isElement(child) && !isBlockTokenElement(child)) {
            Transforms.liftNodes(editor, {
              at: [...path, index]
            });
            return;
          }
        }
      }

      // Lift up token-wrappers that were nested inside other elements
      if (Path.equals(path, [0])) {
        const blockWrapperEntries = findNodes(editor, {
          match: node => isTokenWrapperElement(node)
        });

        // [0] is a common ancestor node to all nodes. If the node is not a direct child of [0], it means it is nested deeper
        // and needs to be lifted up
        const nestedBlockWrappers = blockWrapperEntries.filter(([, blockwrapperPath]) => !Path.isChild(blockwrapperPath, path));
        if (nestedBlockWrappers.length) {
          Transforms.liftNodes(editor, {
            at: path,
            match: matchNode => nestedBlockWrappers.some(([nestedBlockWrapper]) => nestedBlockWrapper === matchNode)
          });
          return;
        }
      }
      normalizeNode(entry);
    };
    return editor;
  },
  renderElement: getRenderElement({
    type: Type,
    rootProps: {},
    component(props) {
      return /*#__PURE__*/React__default.createElement(Token, Object.assign({}, props, {
        getAttributes: getAttributes,
        onClick: onTokenClick,
        renderPopover: renderPopover,
        supportsBlockTokensBeta: supportsBlockTokensBeta,
        enableExperimentalTabNavigation: enableExperimentalTabNavigation
      }));
    }
  }),
  serialize: {
    before(node) {
      if (isTokenElement(node)) {
        const {
          children: [firstChild]
        } = node;

        // Replace text with zero-width space to allow leaf serializers to run
        return {
          ...node,
          children: [{
            ...firstChild,
            text: ZERO_WIDTH_SPACE
          }]
        };
      }
      return undefined;
    },
    element({
      children,
      element
    }) {
      const stringifiedToken = serialize(element);

      // Replace zero-width space with stringified token
      return children ? children.replace(ZERO_WIDTH_SPACE, stringifiedToken) : stringifiedToken;
    }
  },
  deserialize: {
    before: html => {
      const matches = match(html, MatchPhase.Deserialization);
      return matches.reduceRight((acc, {
        value,
        anchor,
        focus,
        block
      }) => {
        const blockValue = Boolean(supportsBlockTokensBeta && block);
        return replace(acc, intermediateHTMLElement(value, blockValue), anchor, focus);
      }, html);
    },
    element: getNodeDeserializer({
      type: Type,
      node: el => {
        const valueAttribute = el.getAttribute(Attribute.Value);
        const blockAttribute = el.getAttribute(Attribute.Block);
        return {
          type: Type,
          value: valueAttribute != null ? decodeURIComponent(valueAttribute) : null,
          block: Boolean(blockAttribute != null && supportsBlockTokensBeta && decodeBlockAttribute(blockAttribute))
        };
      },
      rules: [{
        nodeNames: element.toUpperCase()
      }]
    })
  },
  voidTypes: [Type],
  inlineTypes: [Type]
});
function intermediateHTMLElement(value, block) {
  const blockAttribute = block ? `${Attribute.Block}="enabled"` : '';
  return `<${element} ${Attribute.Value}="${encodeURIComponent(value)}"${blockAttribute}></${element}>`;
}
function decodeBlockAttribute(value) {
  return decodeURIComponent(value) === 'enabled';
}
function replace(string, replacement, anchor, focus) {
  return string.substr(0, anchor) + replacement + string.substr(focus, string.length);
}

export { TokenPlugin };
